import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AGENT_INTERCEPTION_CONFIRM_CONTENT } from '../../constants';
import { AIStatusColors } from '../../redux/features/ai/ai.constants';
import {
  selectAIStatus,
  selectIsAIAutoMode,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectIsAIEscalatedOnCarEnter,
} from '../../redux/features/ai/ai.selector';
import Colors from '../../utils/color';
import ConfirmDialog from '../common/ConfirmDialog';

interface IAIStatusBarStyleProps {
  isAIAutoMode: boolean;
  isAIEscalation: boolean;
  isAIEscalatedOnCarEnter: boolean;
  aiStatusColor: AIStatusColors;
}

interface IAgentInterceptionConfirmDialog {
  open: boolean;
  confirm: boolean;
}

const getStatusBarColor = (
  isAIEscalation: boolean,
  isAIEscalatedOnCarEnter: boolean,
  aiStatusColor: AIStatusColors
) => {
  if (isAIEscalation) {
    return isAIEscalatedOnCarEnter ? Colors.orange : Colors.red;
  } else {
    switch (aiStatusColor) {
      case AIStatusColors.green:
        return Colors.emerald;
      case AIStatusColors.yellow:
        return Colors.gold;
      case AIStatusColors.red:
        return Colors.bittersweet;
      default:
        return Colors.emerald;
    }
  }
};

const useStyles = makeStyles<Theme, IAIStatusBarStyleProps>(({ spacing }) => ({
  aiStatusBarClass: {
    width: '100%',
    height: '48px',
    padding: spacing(1.5),
    color: ({ isAIEscalation }) => (isAIEscalation ? Colors.white : 'inherit'),
    overflowY: 'auto',
  },
  emptyBarClass: {
    fontSize: '1rem',
    display: 'flex',
    height: '100%',
    flexDirection: 'row',
    columnGap: '8px',
    alignItems: 'flex-start',
    lineHeight: '30px',
  },
  boldFontClass: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  aiMsgClass: {
    wordBreak: 'break-all',
  },
}));

const AIStatusBar: FC = (props) => {
  const isAIAutoMode = useSelector(selectIsAIAutoMode);
  const isAIEscalation = useSelector(selectIsAIEscalation);
  const isAIEscalatedOnCarEnter = useSelector(selectIsAIEscalatedOnCarEnter);
  const aiStatus = useSelector(selectAIStatus);
  const aiEscalationReason = useSelector(selectAIEscalationReason);

  const { aiStatusBarClass, emptyBarClass, boldFontClass, aiMsgClass } =
    useStyles({
      isAIAutoMode,
      isAIEscalation,
      isAIEscalatedOnCarEnter,
      aiStatusColor: aiStatus.status,
    });
  const confirmAgentInterception =
    isAIAutoMode && aiStatus.status === AIStatusColors.green && !isAIEscalation;
  const [agentInterceptionConfirmDialog, setAgentInterceptionConfirmDialog] =
    useState<IAgentInterceptionConfirmDialog>({ open: false, confirm: false });

  useEffect(() => {
    if (confirmAgentInterception && !agentInterceptionConfirmDialog.confirm) {
      const handleKeyboardMouseEvent = (event: Event) => {
        // Exclude clicks within the header
        const target = event.target as HTMLElement;
        if (
          target &&
          target.closest &&
          target.closest('[data-header="true"]')
        ) {
          return;
        }
        setAgentInterceptionConfirmDialog((prevState) => ({
          ...prevState,
          open: true,
        }));
      };
      document.addEventListener('keydown', handleKeyboardMouseEvent, true);
      document.addEventListener('mousedown', handleKeyboardMouseEvent, true);
      return () => {
        document.removeEventListener('keydown', handleKeyboardMouseEvent, true);
        document.removeEventListener(
          'mousedown',
          handleKeyboardMouseEvent,
          true
        );
      };
    }
  }, [confirmAgentInterception, agentInterceptionConfirmDialog]);

  const aiStatusBarMessage = isAIEscalation
    ? aiEscalationReason || aiStatus.message
    : aiStatus.message;

  const aiStatusBarBGColor = getStatusBarColor(
    isAIEscalation,
    isAIEscalatedOnCarEnter,
    aiStatus.status
  );

  return (
    <div
      data-testid="ai-status-bar"
      className={aiStatusBarClass}
      style={{
        backgroundColor: aiStatusBarBGColor,
      }}
    >
      <div className={emptyBarClass}>
        <div className={boldFontClass}>AUTO AI: </div>
        <div className={aiMsgClass} title={aiStatusBarMessage}>
          {aiStatusBarMessage}
        </div>
      </div>
      <ConfirmDialog
        open={agentInterceptionConfirmDialog.open}
        content={AGENT_INTERCEPTION_CONFIRM_CONTENT}
        onClose={() =>
          setAgentInterceptionConfirmDialog((prevState) => ({
            ...prevState,
            open: false,
          }))
        }
        onConfirm={() =>
          setAgentInterceptionConfirmDialog({ open: false, confirm: true })
        }
        closeBtnText={'No'}
        confirmBtnText={'Yes'}
      />
    </div>
  );
};

export default AIStatusBar;
